import { Directive, Input, OnInit, ElementRef, Renderer2 } from '@angular/core';
import { UserService } from '../../../auth/user.service';
import { DevicesDashboardService } from '../devices/devices-dashboard.service';

@Directive({
	selector: '[check-permission]'
})
export class CheckPermissionDirective implements OnInit {

	@Input('check-permission') fieldName: string;

	currentUser: any = {};
	userAccess: any = {};

	isBulk: boolean = false;
	deviceType: string = '';

	constructor(
		private el: ElementRef,
		private renderer: Renderer2,
		private devicesDashboard: DevicesDashboardService,
		private userService: UserService
	) {
		this.isBulk = devicesDashboard.isBulkSettings;
		this.deviceType = devicesDashboard.settingsDeviceType;
	}

	ngOnInit() {
		this.currentUser = this.userService.getCurrentUser();
		this.userAccess = this.currentUser.userAccess;

		let accessObj = Object.assign({}, this.userAccess);

		if(this.isBulk && accessObj[this.deviceType + '_BULK'][this.fieldName])
			accessObj = accessObj[this.deviceType + '_BULK'];

		// Get permission value with backward compatibility for spelling corrections
		let permissionValue = this.getPermissionWithFallback(accessObj, this.fieldName);

		if(permissionValue == 'noAccess')
			this.renderer.setStyle(this.el.nativeElement, 'display', 'none');
		else if(permissionValue == 'readOnly')
			this.renderer.setProperty(this.el.nativeElement, 'disabled', 'disabled');
	}

	/**
	 * Get permission value with bidirectional backward compatibility for spelling corrections
	 * Handles both directions since we don't know which spelling the user has in their database
	 */
	private getPermissionWithFallback(accessObj: any, fieldName: string): string {
		// First try the requested spelling
		if(accessObj[fieldName] !== undefined) {
			return accessObj[fieldName];
		}

		// Bidirectional compatibility for _actAccessSSIDPassword <-> _actAccessSSIDpassword
		if(fieldName.includes('_actAccessSSIDPassword')) {
			const legacyFieldName = fieldName.replace('_actAccessSSIDPassword', '_actAccessSSIDpassword');
			if(accessObj[legacyFieldName] !== undefined) {
				return accessObj[legacyFieldName];
			}
		} else if(fieldName.includes('_actAccessSSIDpassword')) {
			const correctFieldName = fieldName.replace('_actAccessSSIDpassword', '_actAccessSSIDPassword');
			if(accessObj[correctFieldName] !== undefined) {
				return accessObj[correctFieldName];
			}
		}

		// Bidirectional compatibility for _mobileAccessSSIDPassword <-> _mobileAccessSSIDpassword
		if(fieldName.includes('_mobileAccessSSIDPassword')) {
			const legacyFieldName = fieldName.replace('_mobileAccessSSIDPassword', '_mobileAccessSSIDpassword');
			if(accessObj[legacyFieldName] !== undefined) {
				return accessObj[legacyFieldName];
			}
		} else if(fieldName.includes('_mobileAccessSSIDpassword')) {
			const correctFieldName = fieldName.replace('_mobileAccessSSIDpassword', '_mobileAccessSSIDPassword');
			if(accessObj[correctFieldName] !== undefined) {
				return accessObj[correctFieldName];
			}
		}

		// Bidirectional compatibility for _onlyForEngineeringTeam <-> _onlyForEnginneringTeam
		if(fieldName.includes('_onlyForEngineeringTeam')) {
			const legacyFieldName = fieldName.replace('_onlyForEngineeringTeam', '_onlyForEnginneringTeam');
			if(accessObj[legacyFieldName] !== undefined) {
				return accessObj[legacyFieldName];
			}
		} else if(fieldName.includes('_onlyForEnginneringTeam')) {
			const correctFieldName = fieldName.replace('_onlyForEnginneringTeam', '_onlyForEngineeringTeam');
			if(accessObj[correctFieldName] !== undefined) {
				return accessObj[correctFieldName];
			}
		}

		// Default to undefined if no permission found
		return undefined;
	}

	/**
	 * Get all alternate spellings for a given permission name
	 * Used for secure permission management to prevent conflicts
	 */
	private getAlternateSpellings(permissionName: string): string[] {
		const alternates: string[] = [];

		if (permissionName.includes('_actAccessSSIDPassword')) {
			alternates.push(permissionName.replace('_actAccessSSIDPassword', '_actAccessSSIDpassword'));
		} else if (permissionName.includes('_actAccessSSIDpassword')) {
			alternates.push(permissionName.replace('_actAccessSSIDpassword', '_actAccessSSIDPassword'));
		}

		if (permissionName.includes('_mobileAccessSSIDPassword')) {
			alternates.push(permissionName.replace('_mobileAccessSSIDPassword', '_mobileAccessSSIDpassword'));
		} else if (permissionName.includes('_mobileAccessSSIDpassword')) {
			alternates.push(permissionName.replace('_mobileAccessSSIDpassword', '_mobileAccessSSIDPassword'));
		}

		if (permissionName.includes('_onlyForEngineeringTeam')) {
			alternates.push(permissionName.replace('_onlyForEngineeringTeam', '_onlyForEnginneringTeam'));
		} else if (permissionName.includes('_onlyForEnginneringTeam')) {
			alternates.push(permissionName.replace('_onlyForEnginneringTeam', '_onlyForEngineeringTeam'));
		}

		return alternates;
	}

	/**
	 * Securely remove permission and all alternate spellings
	 * Prevents security vulnerability where removed permissions persist through legacy spellings
	 */
	private removePermissionSecurely(userAccess: any, permissionName: string): any {
		const newAccess = { ...userAccess };

		// Remove the requested permission
		delete newAccess[permissionName];

		// Remove all alternate spellings to prevent privilege persistence
		const alternates = this.getAlternateSpellings(permissionName);
		alternates.forEach(alt => delete newAccess[alt]);

		return newAccess;
	}

	/**
	 * Securely grant permission, preventing conflicts with alternate spellings
	 * Removes any existing alternate spellings to ensure consistent access control
	 */
	private grantPermissionSecurely(userAccess: any, permissionName: string, value: string): any {
		const newAccess = { ...userAccess };

		// Remove any alternate spellings first to prevent conflicts
		const alternates = this.getAlternateSpellings(permissionName);
		alternates.forEach(alt => delete newAccess[alt]);

		// Grant the permission with the requested spelling
		newAccess[permissionName] = value;

		return newAccess;
	}

	/**
	 * Validate that no conflicting permissions exist in user access
	 * Returns details about any conflicts found
	 */
	private validateNoConflicts(userAccess: any): { hasConflicts: boolean, conflicts: string[] } {
		const conflicts: string[] = [];

		// Check for each type of spelling conflict
		const permissionTypes = [
			{ correct: '_actAccessSSIDPassword', legacy: '_actAccessSSIDpassword' },
			{ correct: '_mobileAccessSSIDPassword', legacy: '_mobileAccessSSIDpassword' },
			{ correct: '_onlyForEngineeringTeam', legacy: '_onlyForEnginneringTeam' }
		];

		Object.keys(userAccess).forEach(permission => {
			permissionTypes.forEach(type => {
				if (permission.includes(type.correct)) {
					const legacyVersion = permission.replace(type.correct, type.legacy);
					if (userAccess[legacyVersion] !== undefined) {
						conflicts.push(`${permission} (${userAccess[permission]}) conflicts with ${legacyVersion} (${userAccess[legacyVersion]})`);
					}
				}
			});
		});

		return { hasConflicts: conflicts.length > 0, conflicts };
	}
}
